import React, { useState } from "react";

const statusColor = (status) => {
  switch (status?.toLowerCase()) {
    case "approved":
      return "bg-emerald-100 text-emerald-700";
    case "rejected":
      return "bg-red-100 text-red-700";
    default:
      return "bg-amber-100 text-amber-700";
  }
};

const DepositLogsTable = ({ deposits }) => {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 5;

  const sortedDeposits = [...deposits].sort(
    (a, b) => b.requestId - a.requestId
  );

  const filteredDeposits = sortedDeposits.filter((deposit) => {
    if (!startDate || !endDate) return true;

    const depositDate = new Date(deposit.requestDate);
    const start = new Date(startDate);
    const end = new Date(endDate);
    end.setHours(23, 59, 59, 999);

    return depositDate >= start && depositDate <= end;
  });

  const totalPages = Math.ceil(filteredDeposits.length / rowsPerPage);

  const paginatedDeposits = filteredDeposits.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  const totalAmount = filteredDeposits.reduce(
    (sum, d) => sum + parseFloat(d.amount || 0),
    0
  );

  return (
    <div className="mt-12">
      {/* ===== HEADER ===== */}
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
        <div>
          <h3 className="text-xl font-semibold text-slate-900">
            Request Value Report
          </h3>
          <p className="text-sm text-slate-500">
            Total Amount: ₹ {totalAmount.toLocaleString()}
          </p>
        </div>

        {/* FILTERS */}
        <div className="flex gap-3">
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="px-4 py-2 rounded-xl bg-slate-100 shadow-inner
              focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="px-4 py-2 rounded-xl bg-slate-100 shadow-inner
              focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>
      </div>

      {/* ===== TABLE CARD ===== */}
      <div
        className="rounded-3xl bg-slate-100 p-6
        shadow-[8px_8px_20px_rgba(0,0,0,0.12),_-8px_-8px_20px_rgba(255,255,255,0.9)]"
      >
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-slate-600">
                {[
                  "Request ID",
                  "Amount",
                  "Request Date",
                  "Deposit Date",
                  "Mode",
                  "Approved Date",
                  "Narration",
                  "Bank",
                  "Status",
                  "Reject Reason",
                ].map((h) => (
                  <th key={h} className="py-3 px-4 font-semibold">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-200">
              {paginatedDeposits.map((d, i) => (
                <tr
                  key={i}
                  className="hover:bg-white/60 transition"
                >
                  <td className="px-4 py-3 font-medium text-slate-800">
                    {d.requestId}
                  </td>
                  <td className="px-4 py-3 text-slate-700">
                    ₹ {d.amount}
                  </td>
                  <td className="px-4 py-3">{d.requestDate}</td>
                  <td className="px-4 py-3">{d.depositDate}</td>
                  <td className="px-4 py-3">{d.mode}</td>
                  <td className="px-4 py-3">
                    {d.approvedDate || "—"}
                  </td>
                  <td className="px-4 py-3">{d.narration}</td>
                  <td className="px-4 py-3">{d.bank}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-semibold
                        ${statusColor(d.action || "Pending")}`}
                    >
                      {d.action || "Pending"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-red-600">
                    {d.rejectReason || "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* ===== PAGINATION ===== */}
        <div className="flex items-center justify-between mt-6">
          <button
            onClick={() =>
              setCurrentPage((p) => Math.max(p - 1, 1))
            }
            disabled={currentPage === 1}
            className="px-4 py-2 rounded-xl bg-slate-100 shadow
              disabled:opacity-50"
          >
            Previous
          </button>

          <span className="text-sm text-slate-600">
            Page {currentPage} of {totalPages}
          </span>

          <button
            onClick={() =>
              setCurrentPage((p) => Math.min(p + 1, totalPages))
            }
            disabled={currentPage === totalPages}
            className="px-4 py-2 rounded-xl bg-slate-100 shadow
              disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default DepositLogsTable;
